import { DOCUMENT } from '@angular/common';
import { Injectable, NgZone, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class AccessibilityService {
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);

  private observer?: MutationObserver;
  private scheduled = false;

  constructor() {
    if (typeof window === 'undefined' || !this.document?.body) {
      return;
    }

    this.zone.runOutsideAngular(() => {
      this.router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe(() => this.scheduleEnhancement());

      this.observer = new MutationObserver(() => this.scheduleEnhancement());
      this.observer.observe(this.document.body, { childList: true, subtree: true });

      this.scheduleEnhancement();
    });
  }

  private scheduleEnhancement(): void {
    if (this.scheduled) {
      return;
    }

    this.scheduled = true;

    const schedule = globalThis.requestAnimationFrame
      ? globalThis.requestAnimationFrame.bind(globalThis)
      : (callback: FrameRequestCallback) => window.setTimeout(() => callback(0), 0);

    schedule(() => {
      this.scheduled = false;
      this.enhanceTree(this.document.body);
    });
  }

  private enhanceTree(root: ParentNode): void {
    this.enhanceInteractiveElements(root);
    this.enhanceLoadingRegions(root);
    this.enhanceOverlayControls(root);
  }

  private enhanceInteractiveElements(root: ParentNode): void {
    const selectors = [
      'button',
      'a[href]',
      'input:not([type="hidden"])',
      'textarea',
      'select',
      '[role="button"]',
      '[role="link"]',
      '.p-menuitem-link',
      '.p-dialog-header-icon',
      '.p-sidebar-close',
      '.p-dropdown',
      '.p-calendar',
      '.p-multiselect',
      '.p-checkbox-box',
      '.p-radiobutton-box',
      '.p-selectbutton .p-button',
    ].join(', ');

    root.querySelectorAll<HTMLElement>(selectors).forEach((element) => {
      if (!this.isVisible(element) || this.isAgGridCell(element)) {
        return;
      }

      const label = this.deriveAccessibleLabel(element);
      if (label && element.getAttribute('aria-label') !== label) {
        element.setAttribute('aria-label', label);
      }
    });
  }

  private enhanceLoadingRegions(root: ParentNode): void {
    const selectors = [
      '.loading-container',
      '.loading-overlay',
      '[data-loading="true"]',
      '.p-progress-spinner',
      '.p-progressbar-indeterminate',
    ].join(', ');

    root.querySelectorAll<HTMLElement>(selectors).forEach((element) => {
      const region = this.resolveLoadingRegion(element);
      if (!region || !this.isVisible(region)) {
        return;
      }

      region.setAttribute('aria-busy', 'true');
      if (!region.hasAttribute('aria-live')) {
        region.setAttribute('aria-live', 'polite');
      }
      if (!region.hasAttribute('role')) {
        region.setAttribute('role', 'status');
      }
      if (!region.hasAttribute('aria-label')) {
        region.setAttribute('aria-label', 'Загрузка...');
      }
    });
  }

  private enhanceOverlayControls(root: ParentNode): void {
    root.querySelectorAll<HTMLElement>('.p-dialog, .p-sidebar').forEach((overlay) => {
      if (!overlay.hasAttribute('aria-modal')) {
        overlay.setAttribute('aria-modal', 'true');
      }
    });
  }

  private resolveLoadingRegion(element: HTMLElement): HTMLElement | null {
    if (element.matches('.loading-container, .loading-overlay, [data-loading="true"]')) {
      return element;
    }

    return element.closest<HTMLElement>('.loading-container, .loading-overlay, [data-loading="true"]');
  }

  private deriveAccessibleLabel(element: HTMLElement): string | null {
    const existingLabel = this.normalizeLabel(element.getAttribute('aria-label'));
    if (existingLabel) {
      return existingLabel;
    }

    const labelledBy = this.normalizeLabel(element.getAttribute('aria-labelledby'));
    if (labelledBy) {
      return null;
    }

    return (
      this.getLabelFromAssociatedLabel(element) ??
      this.getLabelFromContainerContext(element) ??
      this.normalizeLabel(element.getAttribute('title')) ??
      this.normalizeLabel((element as HTMLInputElement).placeholder) ??
      this.normalizeLabel((element as HTMLInputElement).value) ??
      this.getLabelFromImage(element) ??
      this.getLabelFromIcon(element) ??
      this.normalizeLabel(element.textContent) ??
      this.normalizeLabel(element.getAttribute('name')) ??
      this.normalizeLabel(element.id)
    );
  }

  private getLabelFromAssociatedLabel(element: HTMLElement): string | null {
    if (element.id) {
      const safeId = this.escapeSelector(element.id);
      const label = this.document.querySelector(`label[for="${safeId}"]`);
      const labelText = this.normalizeLabel(label?.textContent ?? null);
      if (labelText) {
        return labelText;
      }
    }

    const wrappedLabel = element.closest('label');
    return this.normalizeLabel(wrappedLabel?.textContent ?? null);
  }

  private getLabelFromContainerContext(element: HTMLElement): string | null {
    const containerSelectors = [
      '.field',
      '.parameter-field',
      '.p-field',
      '.p-field-checkbox',
      '.form-field',
      '.filters-panel',
      '.header-actions',
      '.dialog-footer',
      '.grid-header',
    ];

    for (const selector of containerSelectors) {
      const container = element.closest<HTMLElement>(selector);
      const text = this.normalizeLabel(
        container?.querySelector('label, legend, h1, h2, h3, h4, h5, h6, .field-label')?.textContent ??
          null
      );
      if (text) {
        return text;
      }
    }

    return null;
  }

  private getLabelFromImage(element: HTMLElement): string | null {
    const image = element.querySelector('img[alt]') as HTMLImageElement | null;
    return this.normalizeLabel(image?.alt ?? null);
  }

  private getLabelFromIcon(element: HTMLElement): string | null {
    const iconElement = element.matches('[class*="pi-"]')
      ? element
      : element.querySelector<HTMLElement>('[class*="pi-"]');

    const raw: unknown = iconElement?.className;
    const iconClasses = typeof raw === 'string' ? raw : String((raw as { baseVal?: string })?.baseVal ?? '');

    const iconLabels: Array<[string, string]> = [
      ['pi-times', 'Закрыть'],
      ['pi-plus', 'Добавить'],
      ['pi-refresh', 'Обновить'],
      ['pi-save', 'Сохранить'],
      ['pi-search', 'Поиск'],
      ['pi-pencil', 'Редактировать'],
      ['pi-eye', 'Просмотр'],
      ['pi-trash', 'Удалить'],
      ['pi-cog', 'Настройки'],
      ['pi-bell', 'Уведомления'],
      ['pi-comment', 'Комментарии'],
      ['pi-download', 'Скачать'],
      ['pi-upload', 'Загрузить'],
      ['pi-send', 'Отправить'],
      ['pi-play', 'Запустить'],
      ['pi-pause', 'Пауза'],
      ['pi-arrow-left', 'Назад'],
      ['pi-arrow-right', 'Далее'],
      ['pi-external-link', 'Открыть'],
      ['pi-bars', 'Открыть навигацию'],
      ['pi-check-square', 'Выбрать все'],
      ['pi-stop', 'Снять выбор'],
    ];

    for (const [iconClass, label] of iconLabels) {
      if (iconClasses.includes(iconClass)) {
        return label;
      }
    }

    return null;
  }

  private isVisible(element: HTMLElement): boolean {
    if (element.hidden) {
      return false;
    }

    const styles = window.getComputedStyle(element);
    return styles.display !== 'none' && styles.visibility !== 'hidden';
  }

  private isAgGridCell(element: HTMLElement): boolean {
    return Boolean(
      element.closest('.ag-root') &&
        (element.classList.contains('ag-cell') ||
          element.classList.contains('ag-header-cell') ||
          element.getAttribute('role') === 'gridcell' ||
          element.getAttribute('role') === 'columnheader')
    );
  }

  private normalizeLabel(value: string | null | undefined): string | null {
    const normalized = value?.replace(/\s+/g, ' ').trim();
    return normalized ? normalized : null;
  }

  private escapeSelector(value: string): string {
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
      return CSS.escape(value);
    }

    return value.replace(/"/g, '\\"');
  }
}
